import { useDispatch } from "react-redux";
import { useMutation } from "@apollo/client";
import { mdiClockOutline, mdiClose, mdiPhone } from "@mdi/js";
import Icon from "@mdi/react";
import clsx from "clsx";
import dayjs from "dayjs";
import { useToggle } from "usehooks-ts";

import Button from "components/Button";
import ConfirmationModal from "components/ConfirmationModal";
import { ArrowDotRightIcon } from "components/SVGIcons";
import { MUTATION_DELETE_SAVED_TRUCK } from "features/main/Home/SavedPosts/Trucks/query";
import { convertDiffToAge, convertTime, parseError } from "helpers/convertFormData";
import { showErrorMessage, showSuccessMessage } from "helpers/toastMessage";
import { truckInfo, truckLocation } from "helpers/truck";
import { ITruckData } from "models/Truck";
import { AppDispatch } from "store";
import { handleLoading } from "store/global/loader";
import styles from "./style.module.scss";

interface IProps {
  data: ITruckData;
  isSavedTruck?: boolean;
  onClickDetails: (id: string | number) => void;
  refresh?: VoidFunction;
}

export default function TruckItem({
  data,
  isSavedTruck = false,
  onClickDetails,
  refresh
}: Readonly<IProps>) {
  const dispatch = useDispatch<AppDispatch>();
  const [isDeleteConfirmModalOpen, toggleDeleteConfirmModal] = useToggle();

  const {
    id,
    timePosted,
    originCity,
    originRegion,
    originRadius,
    destinationCity,
    user,
    destinationRegion,
    destinationRadius,
    timeStart,
    timeEnd,
    trailerTypes,
    length,
    weight
  } = data;

  const [deleteSavedTruck] = useMutation(MUTATION_DELETE_SAVED_TRUCK, {
    variables: { truckId: id },
    onError(err) {
      err.graphQLErrors.map(({ message }) => showErrorMessage(parseError(message)));
    }
  });

  const truckAge = convertDiffToAge(dayjs().diff(dayjs(timePosted), "milliseconds"));

  const deleteTruck = async () => {
    try {
      dispatch(handleLoading(true));
      const { data } = await deleteSavedTruck({ variables: { truckId: id } });
      if (data.deleteUserSavedTruck.success) {
        showSuccessMessage("Truck deleted successfully.");
      } else {
        showErrorMessage("Failed to delete truck.");
      }
      refresh && refresh();
    } finally {
      dispatch(handleLoading(false));
    }
  };

  return (
    <li className={styles.truckItemCard}>
      {isSavedTruck && (
        <Button
          type="button"
          onClick={toggleDeleteConfirmModal}
          className={styles.closeButton}
          rounded
          iconButton>
          <Icon path={mdiClose} className="m-auto size-2.5" />
        </Button>
      )}
      <div className={clsx(styles.cardHeader, isSavedTruck && "pt-1")}>
        <div className={styles.truckDestination}>
          <span>{truckLocation(originCity, originRegion, originRadius, "origin")}</span>
          <ArrowDotRightIcon className="flex-none" />
          <span>
            {truckLocation(destinationCity, destinationRegion, destinationRadius, "destination")}
          </span>
        </div>
        <span className="text-xs text-text">{truckInfo(trailerTypes, length, weight)}</span>
      </div>

      <ul className={styles.cardContent}>
        <li>
          <span className="text-xs text-text">Ready</span>
          <span>{convertTime(timeStart).formatted}</span>
        </li>

        <li className="pl-3">
          <span className="text-xs text-text">Close</span>
          <span>{convertTime(timeEnd).formatted}</span>
        </li>

        <li>
          <span className="flex items-center justify-end text-xs text-text">
            <Icon path={mdiClockOutline} className="size-3" />
            &nbsp;Age
          </span>
          <span>{truckAge}</span>
        </li>
      </ul>

      <div className={styles.cardFooter}>
        <span className="text-sm capitalize text-text">{user?.dba ?? ""}</span>
        <div className={styles.cardActions}>
          <a href="tel:+496170961709" className={styles.callButton}>
            <Icon path={mdiPhone} className="m-auto text-white" />
          </a>

          <Button onClick={() => onClickDetails(id)} variant="outline" rounded>
            Details
          </Button>
        </div>
      </div>

      <ConfirmationModal
        isConfirmModalOpen={isDeleteConfirmModalOpen}
        toggleConfirmModal={toggleDeleteConfirmModal}
        onOK={deleteTruck}
        content="Are you sure you want to remove this truck from saved?"
        labelOKButton="Delete"
      />
    </li>
  );
}
